import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import {
	ActivityEmailKind,
	applyActivityEmailPreferenceToCurrentUsers,
	enableAllAppLinksForSupportedApps,
	getPageViewModel,
	refreshWorkspaces,
	save,
	setMyWorkspaceAsTemplate,
	setMyWorkspaceAsTemplateForNewAndCurrentUsers,
	WorkspaceSettingsActivityEmailPreferenceSaveModel,
	WorkspaceSettingsModel,
	WorkspaceSettingsViewModel
} from "client/Api/WorkspaceManagement/workspaceManagement";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertDialog,
	Checkbox,
	Form,
	FormattedText,
	FormItem,
	InputWithMoreInfo,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Toggle,
	Toolbar,
	ToolbarAction,
	useAlertDialog,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import styles from "./WorkspaceManagement.module.less";
import {
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation
} from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "../../ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { EMPTY_ARRAY } from "client/Misc/utils";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

enum WorkspaceTemplateMode {
	NewUsers = "0",
	NewAndCurrentUsers = "1"
}

export const WorkspaceManagement = (): ReactElement => {
	const [formData, setFormData] = useState<WorkspaceSettingsModel>(defaultFormData);
	const [isEmbedSharedTileGroupEnabled, setIsEmbedSharedTileGroupEnabled] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [workspaceDefault, setWorkspaceDefault] = useState<WorkspaceTemplateMode>(WorkspaceTemplateMode.NewUsers);
	const [isSharedGroupDialogVisible, setIsSharedGroupDialogVisible] = useState<boolean>(false);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const formHandler: IFormHandler<WorkspaceSettingsModel> =
		useFormHandler<WorkspaceSettingsModel>(
			formData,
			setFormData,
			"NoValidation"
		);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [alertDialog, openAlertDialog] = useAlertDialog();

	const alertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.WorkspaceManagement);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const viewModel: WorkspaceSettingsViewModel = await getPageViewModel(signal)
						.catch(handlePageDataLoadingError);

					setIsEmbedSharedTileGroupEnabled(viewModel.isEmbedSharedTileGroupEnabled);
					setFormData(convertToSaveModel(viewModel));
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const enableAllSupportedApps = useCallback(
		async (): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToEnableThisFunctionality"),
					children: localize("_EnableAllAppLinksForSupportedAppsConfirmationMessage"),
					confirmText: localize("_EnableAllAppLinks"),
					trackingId: "enable-all-supported-apps-confirmation-dialog"
				});
				if (!result.wasSuccessful) {
					return;
				}
				setIsBusy(true);
				await enableAllAppLinksForSupportedApps();
				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullyCreatedAppLinksForAllSupportedApps"));
			} catch(e) {
				setIsBusy(false);
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[alertManager, openConfirmationDialog]
	);

	const setWorkspaceForNewUsers = useCallback(
		async (): Promise<void> => {
			try{
				setIsBusy(true);
				await setMyWorkspaceAsTemplate();
				setIsBusy(false);
				alertManager.showSuccess(localize("_YourCurrentWorkspaceIsSuccessfullySetAsTemplate"));
			} catch(e) {
				setIsBusy(false);
				console.error(e);
				alertManager.showError(localize("_FailedToSetMyWorkspaceAsTemplate"));
			}
		},
		[alertManager]
	);

	const setWorkspaceForNewAndCurrentUsers = useCallback(
		async (): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_SetMyWorkspaceAsTemplateForNewAndCurrentUsers"),
					children: localize("_ThisWillOverwriteAllExistingWorkspacesToMyWorkspace"),
					confirmText: localize("_SetMyWorkspaceAsTemplate"),
					trackingId: "set-workspace-as-template-for-new-and-current-users-confirmation-dialog"
				});
				if (!result.wasSuccessful) {
					return;
				}
				setIsBusy(true);
				await setMyWorkspaceAsTemplateForNewAndCurrentUsers();
				setIsBusy(false);
				alertManager.showSuccess(localize("_YourCurrentWorkspaceIsSuccessfullySetAsTemplate"));
			} catch(e) {
				setIsBusy(false);
				console.error(e);
				alertManager.showError(localize("_FailedToSetMyWorkspaceAsTemplate"));
			}
		},
		[alertManager, openConfirmationDialog]
	);

	const setWorkspaceAsTemplate = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}
			if (workspaceDefault === WorkspaceTemplateMode.NewUsers) {
				setIsBusy(true);
				await setWorkspaceForNewUsers();
				setIsBusy(false);
			} else {
				setIsBusy(true);
				await setWorkspaceForNewAndCurrentUsers();
				setIsBusy(false);
			}
		},
		[
			isBusy,
			setWorkspaceForNewAndCurrentUsers,
			setWorkspaceForNewUsers,
			workspaceDefault
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			const validation: IRootValidation<WorkspaceSettingsModel> = formHandler.validation;
			const validationResult: InputValidationResult = await validation.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				await save(prepareForSaving(formData), globalCancellation);
				setIsBusy(false);
				alertManager.showSuccess(localize("_ChangesSaved"));
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				const isValidationError = validation.tryProcessServerError(e);

				if(isValidationError){
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			formHandler.validation,
			isBusy,
			formData,
			globalCancellation,
			alertManager
		]
	);

	const refreshHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}
			setIsBusy(true);
			try{
				await refreshWorkspaces();
				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessToRefreshWorkspacesAlertMessage"));
			} catch(e) {
				setIsBusy(false);
				console.error(e);
				alertManager.showError(localize("_FailedToRefreshWorkspacesAlertMessage"));
			}
		},
		[isBusy, alertManager]
	);

	const defaultActions: readonly ToolbarAction<undefined>[] = useMemo(
		() => listDefaultActions({
			onSave: saveHandler,
			onRefresh: refreshHandler
		}),
		[refreshHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return buildActionsForTopBar<undefined, ToolbarAction<undefined>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const getEmailToggleValue = (kind: ActivityEmailKind): boolean => {
		return formData.defaultActivityEmailKind === kind;
	};

	const emailToggleChangeHandler = (kind: ActivityEmailKind): ((newValue: boolean) => void) => {
		return (newValue: boolean): void => {
			setFormData(
				formData => {
					if (newValue) {
						if (kind === formData.defaultActivityEmailKind) {
							return formData;
						}

						return {
							...formData,
							defaultActivityEmailKind: kind
						};
					}

					if (kind === formData.defaultActivityEmailKind) {
						return {
							...formData,
							defaultActivityEmailKind: null
						};
					}

					return formData;
				}
			);
		};
	};

	const forceDefaultStateToAllUsersHandler = async (): Promise<void> => {
		const result: TryResult<void> = await openConfirmationDialog({
			title: localize("_AreYouSureYouWantToForceTheDefaultStateToAllUsers"),
			children: localize("_ForceTheDefaultStateToAllUsersConfirmationMessage"),
			confirmText: localize("_ForceDefaultState"),
			trackingId: "force-default-state-to-all-users-confirmation-dialog"
		});

		if (!result.wasSuccessful) {
			return;
		}

		try {
			setIsBusy(true);

			await applyActivityEmailPreferenceToCurrentUsers(
				prepareForApplyingActivityEmailPreferencesToCurrentUsers(formData),
				globalCancellation
			);

			setIsBusy(false);
			alertManager.showSuccess(localize("_SuccessfullyForcedTheDefaultStateToAllUsers"));
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);
			alertManager.showError(localize("_GeneralErrorMessage"));
		}
	};

	const showDailyWeeklyMoreInfoDialog = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_DailyWeeklyEmailSummary"),
				children: (
					<FormattedText
						text={localize("_DailyWeeklyEmailSummaryInfoMessage")}
						thingsToReplace={EMPTY_ARRAY}
					/>
				),
				showCloseButton: true,
				trackingId: "email-summary-info-dialog"
			});
		},
		[openAlertDialog]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={undefined}
				title={localize("_WorkspaceManagement")}
			>
				<Form onSubmit="NONE" hasWidthConstrained>
					{isEmbedSharedTileGroupEnabled && (
						<SettingsPage.Section title={localize("_TileGroups")}>
							<FormItem>
								<Checkbox
									{
										...formHandler
											.for("isPercentileTileScalingEnabledForEmbedTileGroup")
											.attachToCheckbox()
									}
									text={localize("_EnableResponsiveEmbedTileGroup")}
									ellipseText={false}
								/>
								<ValidationError
									{...formHandler
											.for("isPercentileTileScalingEnabledForEmbedTileGroup")
											.attachToValidationError()
									}
								/>
							</FormItem>
						</SettingsPage.Section>
					)}

					<SettingsPage.Section title={localize("_PersonalGroups")}>
						<p>{localize("_WorkspaceTemplateManagementSetMyWorkspaceAsDefaultExplanation")}</p>
						<FormItem isForGroup>
							<RadioButtonGroup
								options={workspaceTemplateRadioGroupOptions}
								value={workspaceDefault}
								onChange={setWorkspaceDefault}
								trackingId="workspace-as-template-management-button"
							/>
						</FormItem>
						<FormItem>
							<Toolbar>
								<Toolbar.Button
									kind="primary"
									text={localize("_SetMyWorkspaceAsTemplate")}
									onClick={() => setWorkspaceAsTemplate()}
									trackingId="set-workspace-as-template-button"
								/>
							</Toolbar>
						</FormItem>
					</SettingsPage.Section>

					<SettingsPage.Section title={localize("_SharedGroups")}>
						<FormItem>
							<InputWithMoreInfo
								onMoreInfo={() => setIsSharedGroupDialogVisible(true)}
								trackingId="shared-group-info-button"
							>
								<Checkbox
									{
										...formHandler
											.for("automaticallyReorderTilesForNoneEditableTileGroups")
											.attachToCheckbox()
									}
									text={localize("_AutomaticallyReorderTilesWithinSharedGroupsForUsers")}
									ellipseText={false}
								/>
								{
									isSharedGroupDialogVisible && (
										<AlertDialog
											title={localize("_HowDoesTheReorderingWork")}
											onClose={() => setIsSharedGroupDialogVisible(false)}
											trackingId="shared-grouo-dialog"
											showCloseButton
										>
											{localize("_AutomaticTileReorderingExplanation")}
											<div className={styles.automaticTileReorderingImage}>
												<h4 className={styles.adminView}>{localize("_AdminView")}</h4>
												<h4 className={styles.userViewNoReordering}>{localize("_UserViewNoReordering")}</h4>
												<h4 className={styles.userViewReorderingApplied}>{localize("_UserViewReorderingApplied")}</h4>
											</div>
										</AlertDialog>
									)
								}
								<ValidationError
									{...formHandler
											.for("automaticallyReorderTilesForNoneEditableTileGroups")
											.attachToValidationError()
									}
								/>
							</InputWithMoreInfo>
						</FormItem>
					</SettingsPage.Section>

					<SettingsPage.Section title={localize("_AppLinks")}>
						<FormItem>
							<Toolbar>
								<Toolbar.Button
									kind="primary"
									text={localize("_EnableAllAppLinksForSupportedApps")}
									onClick={enableAllSupportedApps}
									trackingId="app-link-button"
								/>
							</Toolbar>
						</FormItem>
					</SettingsPage.Section>

					<SettingsPage.Section title={localize("_FrequentlyUsedApps")}>
						<FormItem>
							<Checkbox
								{
									...formHandler
										.for("isFrequentlyUsedTilesGroupEnabled")
										.attachToCheckbox()
								}
								text={localize("_EnableFrequentlyUsedApps")}
								ellipseText={false}
							/>
							<ValidationError
								{...formHandler
										.for("isFrequentlyUsedTilesGroupEnabled")
										.attachToValidationError()
								}
							/>
						</FormItem>
					</SettingsPage.Section>

					<SettingsPage.Section title={localize("_DailyWeeklyEmailSummary")}>
						<FormItem>
							<InputWithMoreInfo
								onMoreInfo={showDailyWeeklyMoreInfoDialog}
								trackingId="daily-weekly-more-info"
							>
								<Checkbox
									{...formHandler.for("areActivityEmailsEnabled").attachToCheckbox()}
									text={localize("_EnableDailyWeeklyEmailSummaryOfWorkspaceActivities")}
								/>
							</InputWithMoreInfo>
							<ValidationError
								{...formHandler.for("areActivityEmailsEnabled").attachToValidationError()}
							/>
						</FormItem>
						{formData.areActivityEmailsEnabled && (
							<>
								<FormItem
									label={localize("_DefaultState")}
									isForGroup
								>
									<div className={styles.toggleWithMessage}>
										<Toggle
											value={getEmailToggleValue(ActivityEmailKind.Daily)}
											onChange={emailToggleChangeHandler(ActivityEmailKind.Daily)}
											text={localize("_DailySummaryOfWorkspaceActivities")}
										/>
									</div>
									<div className={styles.toggleWithMessage}>
										<Toggle
											value={getEmailToggleValue(ActivityEmailKind.Weekly)}
											onChange={emailToggleChangeHandler(ActivityEmailKind.Weekly)}
											text={localize("_WeeklySummaryOfWorkspaceActivities")}
										/>
									</div>
								</FormItem>
								<FormItem>
									<Toolbar>
										<Toolbar.Button
											kind="primary"
											text={localize("_ForceDefaultStateToAllUsers")}
											onClick={forceDefaultStateToAllUsersHandler}
										/>
									</Toolbar>
								</FormItem>
							</>
						)}
					</SettingsPage.Section>
				</Form>
				<div className="preserve-space-for-mobile-plus-button" />
				<AiAssistantFabSpacing />
			</SettingsPage>
			{confirmationDialog}
			{alertDialog}
		</>
	);
};
WorkspaceManagement.displayName = "WorkspaceManagement";

const workspaceTemplateRadioGroupOptions: readonly RadioButtonGroupItem<WorkspaceTemplateMode>[] = [
	{
		text: localize("_NewUsers"),
		value: WorkspaceTemplateMode.NewUsers
	},
	{
		text: localize("_NewAndCurrentUsers"),
		value: WorkspaceTemplateMode.NewAndCurrentUsers
	}
];

type DefaultActionsHandlers = {
	onSave(): void;
	onRefresh(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly ToolbarAction<undefined>[]{
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.onSave(),
			icon: "floppy-disk",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "refreshWorkspaces",
			title: localize("_RefreshWorkspaces"),
			canExecute: () => true,
			execute: () => handlers.onRefresh(),
			icon: "arrow-rotate-right"
		}
	];
}

function convertToSaveModel(initialData: WorkspaceSettingsViewModel): WorkspaceSettingsModel {
	return { ...initialData.model };
};

function prepareForSaving(model: WorkspaceSettingsModel): WorkspaceSettingsModel {
	if (!model.areActivityEmailsEnabled) {
		model = {
			...model,
			defaultActivityEmailKind: null
		};
	}

	return model;
}

function prepareForApplyingActivityEmailPreferencesToCurrentUsers(
	model: WorkspaceSettingsModel
): WorkspaceSettingsActivityEmailPreferenceSaveModel {
	return {
		activityEmailKind: model.defaultActivityEmailKind
	};
}

const defaultFormData: WorkspaceSettingsModel = {
	automaticallyReorderTilesForNoneEditableTileGroups: false,
	isFrequentlyUsedTilesGroupEnabled: true,
	isPercentileTileScalingEnabledForEmbedTileGroup: false,
	areActivityEmailsEnabled: false,
	defaultActivityEmailKind: null
};

export const workspaceManagementRouting = {
	page: "/WorkspaceManagement"
};
